if (!customElements.get('lazy-section-template')) {
  const COMPONENT_VERSION = '2026-09-28-v21-heights-anchor';

  const HEIGHTS_KEY = 'triumph:lazy-heights:v1';
  const HEIGHTS_MAX_ENTRIES = 400;
  const HEIGHT_QUIET_MS = 700;
  const HEIGHT_TRACK_MAX_MS = 6000;
  const heightBucket = () => {
    const width = window.innerWidth;
    const band = width <= 576 ? 'm' : width <= 1024 ? 't' : 'd';
    return `${band}${Math.round(width / 40) * 40}`;
  };
  const heightKeyFor = (sectionId) => (sectionId ? `${sectionId}@${heightBucket()}` : '');
  let heightCache = null;
  let heightWriteTimer = 0;

  const getHeights = () => {
    if (heightCache) return heightCache;

    try {
      heightCache = JSON.parse(window.localStorage.getItem(HEIGHTS_KEY)) || {};
    } catch (e) {
      heightCache = {};
    }

    return heightCache;
  };

  const saveHeight = (key, height) => {
    if (!key || !(height > 0)) return;

    const heights = getHeights();
    if (!(key in heights) && Object.keys(heights).length >= HEIGHTS_MAX_ENTRIES) heightCache = {};
    getHeights()[key] = Math.round(height);

    clearTimeout(heightWriteTimer);
    heightWriteTimer = setTimeout(() => {
      try {
        window.localStorage.setItem(HEIGHTS_KEY, JSON.stringify(heightCache));
      } catch (e) {}
    }, 500);
  };

  const ANCHOR_KEY = 'triumphLazyAnchor';

  const findScrollAnchor = () => {
    const sections = document.querySelectorAll('.shopify-section');

    for (let i = 0; i < sections.length; i += 1) {
      const section = sections[i];
      const { position } = window.getComputedStyle(section);

      if (!section.id || position === 'sticky' || position === 'fixed') continue;

      const { top, bottom } = section.getBoundingClientRect();
      if (bottom > 1) return { id: section.id, offset: Math.round(Math.max(0, -top)) };
    }

    return null;
  };

  const saveScrollAnchor = () => {
    const base = history.state;
    if (base !== null && typeof base !== 'object') return;

    const anchor = window.scrollY > 0 ? findScrollAnchor() : null;

    try {
      history.replaceState({ ...base, [ANCHOR_KEY]: anchor }, '');
    } catch (e) {}
  };

  const scrollByInstant = (top) => {
    try {
      window.scrollBy({ top, behavior: 'instant' });
    } catch (e) {
      window.scrollBy(0, top);
    }
  };

  const restoreScrollAnchor = () => {
    const nav = performance.getEntriesByType('navigation')[0];
    if (nav?.type !== 'back_forward' && nav?.type !== 'reload') return;

    const saved = history.state && history.state[ANCHOR_KEY];
    const target = saved && saved.id ? document.getElementById(saved.id) : null;
    if (!target) return;

    history.scrollRestoration = 'manual';

    document.querySelectorAll('lazy-section-template').forEach((el) => {
      if (el.hydrated || el.dataset.tabLazy === '1') return;

      const isAbove = Boolean(target.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_PRECEDING);
      if (target.contains(el) || (isAbove && !el.reservedHeight)) el.hydrate('restore_anchor');
    });

    let userMoved = false;
    let follow = null;
    const markMoved = () => {
      userMoved = true;
      follow?.disconnect();
    };
    ['wheel', 'touchstart', 'keydown', 'mousedown'].forEach((type) => {
      window.addEventListener(type, markMoved, { passive: true, once: true });
    });

    const align = () => {
      if (userMoved) return;

      const drift = target.getBoundingClientRect().top + saved.offset;
      if (Math.abs(drift) > 1) scrollByInstant(drift);
    };

    align();

    [600, 1800].forEach((ms) => setTimeout(align, ms));
    if (document.readyState !== 'complete') window.addEventListener('load', align, { once: true });

    if ('ResizeObserver' in window) {
      let frame = 0;

      follow = new ResizeObserver(() => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(align);
      });
      follow.observe(document.documentElement);
      setTimeout(() => follow.disconnect(), 8000);
    }
  };

  const initScrollAnchor = () => {
    if (window.Shopify && Shopify.designMode) return;
    if (!document.querySelector('lazy-section-template:not([data-tab-lazy])')) return;

    let saveTimer = 0;
    let scrolledSinceSave = false;

    const save = () => {
      clearTimeout(saveTimer);
      scrolledSinceSave = false;
      saveScrollAnchor();
    };

    try {
      restoreScrollAnchor();
    } catch (e) {}

    window.addEventListener(
      'scroll',
      () => {
        scrolledSinceSave = true;
        clearTimeout(saveTimer);
        saveTimer = setTimeout(save, 250);
      },
      { passive: true }
    );
    window.addEventListener('pagehide', () => {
      if (scrolledSinceSave) save();
    });
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden' && scrolledSinceSave) save();
    });
  };

  class LazySectionTemplate extends HTMLElement {
    static hydrateUpcoming(fromSection, count = 3) {
      let section = fromSection?.nextElementSibling;

      for (let i = 0; i < count && section; i += 1) {
        const lazySection = section.querySelector('lazy-section-template');

        if (
          lazySection instanceof LazySectionTemplate &&
          !lazySection.hydrated &&
          lazySection.dataset.tabLazy !== '1' &&
          lazySection.dataset.requireScroll !== '1'
        ) {
          lazySection.hydrateWhenIdle('preload_from_media_scroll');
        }

        section = section.nextElementSibling;
      }
    }

    static followingSections(fromSection, skip) {
      const sections = [];
      let section = fromSection;

      for (let i = 0; i <= skip && section; i += 1) {
        section = section.nextElementSibling;
      }

      while (section) {
        if (section.classList.contains('shopify-section')) sections.push(section);
        section = section.nextElementSibling;
      }

      return sections;
    }

    static holdLazyAfter(fromSection, skip = 1) {
      LazySectionTemplate.followingSections(fromSection, skip).forEach((section) => {
        section.dataset.lazyHold = String(Number(section.dataset.lazyHold || 0) + 1);
      });
    }

    static releaseLazyAfter(fromSection, skip = 1) {
      LazySectionTemplate.followingSections(fromSection, skip).forEach((section) => {
        const count = Number(section.dataset.lazyHold || 0) - 1;

        if (count > 0) {
          section.dataset.lazyHold = String(count);
        } else {
          delete section.dataset.lazyHold;
        }
      });
    }

    connectedCallback() {
      this.template = this.querySelector('template');
      if (!this.template) return;

      this.preloadOffset = Number(this.dataset.preloadOffset || 200);
      this.requireScroll = this.dataset.requireScroll === '1';
      this.hasUserScrolled = window.scrollY > 0;
      this.debug = this.dataset.debug === '1' || window.location.search.includes('lazyDebug=1');
      this.hydrated = false;
      this.observer = null;
      this.pendingHydrateOnScroll = false;
      this.host = this.parentElement?.classList.contains('shopify-section') ? this.parentElement : null;
      this.heightKey = heightKeyFor(this.dataset.sectionId);
      this.reservedHeight = false;

      this.onIntersect = this.onIntersect.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.onSettle = this.onSettle.bind(this);

      this.log('init', {
        version: COMPONENT_VERSION,
        preloadOffset: this.preloadOffset,
        requireScroll: this.requireScroll,
        viewportHeight: window.innerHeight || 0,
        rect: this.debug ? this.getBoundingClientRect() : null
      });

      if (window.Shopify && Shopify.designMode) {
        this.hydrate('design_mode');
        return;
      }

      if (this.dataset.tabLazy === '1') {
        this.log('tab_lazy_mode — waiting for external hydrate call');
        return;
      }

      this.reserveHeight();

      window.addEventListener('scroll', this.onScroll, { passive: true });

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(this.onIntersect, {
          root: null,
          rootMargin: `${this.preloadOffset}px 0px ${this.preloadOffset}px 0px`,
          threshold: 0
        });
        this.observer.observe(this);
      } else {
        this.hydrate('observer_unavailable');
      }

      if (document.readyState === 'complete') {
        requestAnimationFrame(this.onSettle);
      } else {
        window.addEventListener('load', this.onSettle, { once: true });
      }
    }

    disconnectedCallback() {
      this.cleanup();
    }

    onScroll() {
      this.hasUserScrolled = true;
      if (!this.pendingHydrateOnScroll || this.hydrated) return;
      this.pendingHydrateOnScroll = false;
      this.hydrate('observer_intersect_on_first_scroll_event');
    }

    hydrateWhenIdle(reason) {
      if (this.hydrated || this.hydrateScheduled) return;
      this.hydrateScheduled = true;

      const run = () => this.hydrate(reason);

      if ('requestIdleCallback' in window) {
        requestIdleCallback(run, { timeout: 500 });
      } else {
        setTimeout(run, 50);
      }
    }

    onSettle() {
      if (this.hydrated) return;

      const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
      const { top, bottom } = this.getBoundingClientRect();
      const isVisibleInViewport = top <= viewportHeight && bottom >= 0;

      this.log('settle_recheck', { isVisibleInViewport, top, bottom, viewportHeight });

      if (isVisibleInViewport) this.hydrate('settle_recheck_visible_after_layout');
    }

    onIntersect(entries) {
      this.log('intersect_entries_count', entries.length);
      for (let i = 0; i < entries.length; i += 1) {
        const entry = entries[i];

        this.log('intersect', {
          isIntersecting: entry.isIntersecting,
          intersectionRatio: entry.intersectionRatio,
          targetIsSelf: entry.target === this,
          boundingClientRect: entry.boundingClientRect,
          rootBounds: entry.rootBounds
        });

        if (!entry.isIntersecting) continue;
        if (this.requireScroll && !this.hasUserScrolled && window.scrollY <= 0) {
          this.log('intersect_ignored_before_first_scroll');
          continue;
        }

        const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
        const { top, bottom } = entry.boundingClientRect;
        const isVisibleInViewport = top <= viewportHeight && bottom >= 0;

        if (isVisibleInViewport) {
          this.hydrate('observer_intersect_visible_in_viewport');
          break;
        }

        if (window.scrollY <= 0) {
          this.pendingHydrateOnScroll = true;
          this.log('intersect_deferred_until_scroll');
          continue;
        }

        this.hydrate('observer_intersect');
        break;
      }
    }

    hydrate(reason = 'hydrate') {
      if (this.hydrated || !this.template || !this.template.parentNode) return;
      this.hydrated = true;
      this.log('hydrate', reason, this.debug ? { rect: this.getBoundingClientRect() } : undefined);

      this.insertBefore(this.template.content.cloneNode(true), this.template);
      this.template.remove();
      this.style.minHeight = '';

      this.cleanup();
      this.dispatchEvent(new CustomEvent('lazy-section-hydrated', { bubbles: true }));

      this.trackHeight();
    }

    reserveHeight() {
      if (this.dataset.tabLazy === '1') return;

      if (this.host) this.host.style.minHeight = '';
      this.style.minHeight = '';
      this.reservedHeight = false;
      this.heightKey = heightKeyFor(this.dataset.sectionId);

      const cached = this.heightKey ? getHeights()[this.heightKey] : 0;
      if (!(cached > 0)) return;

      if (this.host) {
        this.host.style.minHeight = `${cached}px`;
        this.style.minHeight = '0px';
      } else {
        this.style.minHeight = `${cached}px`;
      }

      this.reservedHeight = true;
    }

    releaseHeight() {
      if (this.host) this.host.style.minHeight = '';
    }

    trackHeight() {
      if (!this.heightKey || this.dataset.tabLazy === '1' || (window.Shopify && Shopify.designMode)) return;

      if (!('ResizeObserver' in window)) {
        this.releaseHeight();
        return;
      }

      const target = this.host || this;
      const key = heightKeyFor(this.dataset.sectionId);
      let released = !(this.reservedHeight && this.host);
      let quietTimer = 0;
      let lastHeight = 0;

      const finish = () => {
        observer.disconnect();
        clearTimeout(quietTimer);
        clearTimeout(capTimer);
        this.releaseHeight();

        if (heightKeyFor(this.dataset.sectionId) !== key) return;

        const height = this.host && lastHeight ? lastHeight : target.getBoundingClientRect().height;
        saveHeight(key, height);

        this.log('height_saved', { key, height: Math.round(height), reserved: this.reservedHeight });
      };

      const onQuiet = () => {
        if (released) {
          finish();
          return;
        }

        released = true;
        this.releaseHeight();
        quietTimer = setTimeout(onQuiet, HEIGHT_QUIET_MS);
      };

      const observer = new ResizeObserver((entries) => {
        const entry = entries[entries.length - 1];
        lastHeight = entry.borderBoxSize?.[0]?.blockSize ?? entry.contentRect.height;

        clearTimeout(quietTimer);
        quietTimer = setTimeout(onQuiet, HEIGHT_QUIET_MS);
      });
      const capTimer = setTimeout(finish, HEIGHT_TRACK_MAX_MS);

      observer.observe(target);
      quietTimer = setTimeout(onQuiet, HEIGHT_QUIET_MS);
    }

    log(...args) {
      if (!this.debug) return;
      const id = this.dataset.sectionId || this.id || 'unknown';
      console.debug('[lazy-section-template]', id, ...args);
    }

    cleanup() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('load', this.onSettle);
    }
  }

  customElements.define('lazy-section-template', LazySectionTemplate);

  initScrollAnchor();
}
